// Reactive synthwave soundtrack, generated entirely in the Web Audio API — no
// asset files (keeps the Cloudflare static bundle tiny).
//
// Era-authentic 80s / synthwave production, adapted live to the game via
// VERTICAL LAYERING (layers fade in/out instead of hard cuts). Three scenes:
//
//   menu — zen & peaceful: lush detuned pads + sparse FM-bell arpeggio, no drums
//   play — synthwave drive: gallop bass, gated-style drums, lead; INTENSITY rises
//          as the zone timer approaches zero (more hats, denser bass, lead motif)
//   boss — intense: faster & darker progression, driving bass, busy drums, lead
//
// Production touches per the research: detuned-saw analog width, Juno-style pads,
// crystalline 2-op FM ("DX7") bell lead, sidechain PUMP ducking synths under the
// kick, a slap echo on the lead, and slight per-note tuning drift.

function midi(n: number): number {
  return 440 * Math.pow(2, (n - 69) / 12);
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

export type Scene = "menu" | "play" | "boss";

interface SceneDef {
  bpm: number;
  vol: number;
  filterBase: number;
  filterRange: number;
  roots: number[];
  triads: number[][];
}

// Each scene is a 4-bar loop. Menu = dreamy maj7/min7; play = classic Am–F–C–G;
// boss = a darker D-minor vamp with an A-major V for tension.
const SCENES: Record<Scene, SceneDef> = {
  menu: {
    bpm: 84,
    vol: 0.3,
    filterBase: 480,
    filterRange: 900,
    roots: [53, 57, 50, 55],
    triads: [
      [53, 57, 60, 64], // Fmaj7
      [57, 60, 64, 67], // Am7
      [50, 53, 57, 60], // Dm7
      [55, 59, 62, 66], // Gmaj7-ish
    ],
  },
  play: {
    bpm: 112,
    vol: 0.4,
    filterBase: 600,
    filterRange: 3400,
    roots: [45, 41, 48, 43],
    triads: [
      [45, 48, 52],
      [41, 45, 48],
      [48, 52, 55],
      [43, 47, 50],
    ],
  },
  boss: {
    bpm: 138,
    vol: 0.46,
    filterBase: 1100,
    filterRange: 4200,
    roots: [50, 46, 48, 45],
    triads: [
      [50, 53, 57], // Dm
      [46, 50, 53], // Bb
      [48, 52, 55], // C
      [45, 49, 52], // A (major V — tension)
    ],
  },
};

const LEAD_PENTA = [0, 3, 5, 7, 10]; // A-minor pentatonic offsets

export class Music {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null; // overall volume / mute
  private pump: GainNode | null = null; // sidechain duck (synths only)
  private filter: BiquadFilterNode | null = null; // tone, opens with intensity
  private synthBus: GainNode | null = null; // post-filter synth sum
  private drumBus: GainNode | null = null; // drums (bypass filter+pump → punchy)
  private sfxBus: GainNode | null = null; // game SFX (direct to output, crisp)
  private echo: DelayNode | null = null; // lead slap echo
  private noiseBuf: AudioBuffer | null = null;
  private lastShootAt = 0; // throttle for the shoot blip

  private playing = false;
  private muted = false;
  private unlocked = false;

  private step = 0;
  private bar = 0;
  private nextTime = 0;

  private scene: Scene = "menu";
  private bpm = SCENES.menu.bpm;
  private volume = SCENES.menu.vol;
  private intensity = 0; // smoothed
  private intensityTarget = 0;

  private ensure(): void {
    if (this.ctx) return;
    const Ctx =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx();

    this.master = this.ctx.createGain();
    this.master.gain.value = 0;
    this.master.connect(this.ctx.destination);

    this.pump = this.ctx.createGain(); // sidechain target
    this.pump.gain.value = 1;
    this.pump.connect(this.master);

    this.synthBus = this.ctx.createGain();
    this.synthBus.gain.value = 1;
    this.synthBus.connect(this.pump);

    this.filter = this.ctx.createBiquadFilter();
    this.filter.type = "lowpass";
    this.filter.frequency.value = SCENES[this.scene].filterBase;
    this.filter.Q.value = 6;
    this.filter.connect(this.synthBus);

    // Slap echo for the lead (dotted-8th feedback delay → back into the filter bus).
    this.echo = this.ctx.createDelay(1.0);
    this.echo.delayTime.value = (60 / this.bpm) * 0.75;
    const echoFb = this.ctx.createGain();
    echoFb.gain.value = 0.32;
    this.echo.connect(echoFb);
    echoFb.connect(this.echo);
    this.echo.connect(this.synthBus);

    this.drumBus = this.ctx.createGain();
    this.drumBus.gain.value = 0.9;
    this.drumBus.connect(this.master);

    // Game SFX go straight to the output (crisp, not filtered/ducked by the
    // music). Mute is handled by early-returning in the sfx methods.
    this.sfxBus = this.ctx.createGain();
    this.sfxBus.gain.value = 0.6;
    this.sfxBus.connect(this.ctx.destination);

    const len = Math.floor(this.ctx.sampleRate * 0.5);
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    this.noiseBuf = buf;
  }

  /**
   * Create + resume the audio context from inside a real user-gesture handler.
   * Mobile browsers (esp. iOS Safari) only unlock audio within the gesture's
   * synchronous call stack — and the game's start tap is processed a frame later
   * in the loop, which doesn't count — so this is wired to DOM gesture events
   * directly. Idempotent and cheap; safe to call on every gesture (also re-
   * resumes if the OS suspended the context while backgrounded).
   */
  unlock(): void {
    this.ensure();
    if (!this.ctx) return;
    if (this.ctx.state !== "running") void this.ctx.resume().catch(() => {});
    if (this.unlocked) return;
    this.unlocked = true;
    try {
      const src = this.ctx.createBufferSource();
      src.buffer = this.ctx.createBuffer(1, 1, this.ctx.sampleRate);
      src.connect(this.ctx.destination);
      src.start(0);
    } catch {
      /* ignore */
    }
  }

  /** Must be called from a user gesture (browser autoplay policy). */
  async start(): Promise<void> {
    this.ensure();
    if (!this.ctx || !this.master) return;
    await this.ctx.resume().catch(() => {});
    if (this.playing) return;
    this.playing = true;
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(this.master.gain.value, now);
    this.master.gain.linearRampToValueAtTime(this.muted ? 0 : this.volume, now + 1.2);
    this.nextTime = now + 0.1;
    this.loop();
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.ctx && this.master) {
      const now = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(now);
      this.master.gain.linearRampToValueAtTime(this.muted ? 0 : this.volume, now + 0.2);
    }
    return this.muted;
  }

  /** Drive the soundtrack from the game: which scene, and how intense (0..1). */
  setScene(scene: Scene, intensity: number): void {
    this.intensityTarget = clamp01(intensity);
    if (scene !== this.scene) this.changeScene(scene);
  }

  private changeScene(scene: Scene): void {
    this.scene = scene;
    const sc = SCENES[scene];
    this.bpm = sc.bpm;
    this.volume = sc.vol;
    if (this.ctx && this.master) {
      const t = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(t);
      this.master.gain.setValueAtTime(this.master.gain.value, t);
      this.master.gain.linearRampToValueAtTime(this.muted ? 0 : sc.vol, t + 1.0);
    }
    if (this.echo && this.ctx) {
      this.echo.delayTime.setTargetAtTime((60 / this.bpm) * 0.75, this.ctx.currentTime, 0.1);
    }
  }

  // ── SFX ─────────────────────────────────────────────────────────────────────

  /**
   * Short shoot blip. Pitched to the current scene's pentatonic (so rapid fire
   * reads as a complementary arpeggio, not noise) and throttled so auto-fire
   * doesn't pile up. Soft triangle "pew" with a quick downward chirp.
   */
  sfxShoot(): void {
    if (!this.ctx || this.muted || !this.sfxBus) return;
    const t = this.ctx.currentTime;
    if (t - this.lastShootAt < 0.08) return; // throttle (~12/sec max)
    this.lastShootAt = t;

    const sc = SCENES[this.scene];
    const root = sc.roots[this.bar % sc.roots.length];
    const deg = LEAD_PENTA[(this.step + this.bar) % LEAD_PENTA.length];
    const freq = midi(root + 24 + deg); // two octaves above the chord root
    const dur = 0.09;

    const o = this.ctx.createOscillator();
    o.type = "triangle";
    o.frequency.setValueAtTime(freq, t);
    o.frequency.exponentialRampToValueAtTime(freq * 0.6, t + dur); // chirp down
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.09, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g);
    g.connect(this.sfxBus);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  /**
   * SURGE / Communication Purge payoff: a rising filtered-noise whoosh, a low
   * impact boom, and a bright shimmering FM-bell chord (with echo) — satisfying
   * and "nice" without being harsh.
   */
  sfxSurge(): void {
    if (!this.ctx || this.muted || !this.sfxBus) return;
    const t = this.ctx.currentTime;

    // Rising whoosh.
    if (this.noiseBuf) {
      const src = this.ctx.createBufferSource();
      src.buffer = this.noiseBuf;
      src.loop = true;
      const bp = this.ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.Q.value = 1.2;
      bp.frequency.setValueAtTime(300, t);
      bp.frequency.exponentialRampToValueAtTime(6500, t + 0.5);
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.45, t + 0.18);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.8);
      src.connect(bp);
      bp.connect(g);
      g.connect(this.sfxBus);
      src.start(t);
      src.stop(t + 0.85);
    }

    // Low impact boom.
    const o = this.ctx.createOscillator();
    o.type = "sine";
    o.frequency.setValueAtTime(90, t);
    o.frequency.exponentialRampToValueAtTime(40, t + 0.3);
    const bg = this.ctx.createGain();
    bg.gain.setValueAtTime(0.5, t);
    bg.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
    o.connect(bg);
    bg.connect(this.sfxBus);
    o.start(t);
    o.stop(t + 0.45);

    // Bright triumphant chord (C major), shimmering with echo.
    const chord = [60, 64, 67, 72];
    chord.forEach((n, i) => this.fmBell(midi(n), t + 0.06 + i * 0.03, 0.9, 0.12, 2, 90, true, this.sfxBus!));
  }

  private loop = (): void => {
    if (!this.playing || !this.ctx || !this.filter) return;

    // Smoothly approach the target intensity (vertical layering crossfade).
    this.intensity += (this.intensityTarget - this.intensity) * 0.06;

    // Filter cutoff opens with intensity; range/base depend on the scene, so the
    // track "wakes up" as the timer drops or a boss appears.
    const sc = SCENES[this.scene];
    const cutoff = sc.filterBase + this.intensity * sc.filterRange;
    this.filter.frequency.setTargetAtTime(cutoff, this.ctx.currentTime, 0.2);

    const sixteenth = 60 / this.bpm / 4;
    while (this.nextTime < this.ctx.currentTime + 0.15) {
      this.scheduleStep(this.step, this.nextTime);
      this.step = (this.step + 1) % 16;
      if (this.step === 0) this.bar = (this.bar + 1) % 4;
      this.nextTime += sixteenth;
    }
    window.setTimeout(this.loop, 25);
  };

  private scheduleStep(step: number, time: number): void {
    if (this.scene === "menu") return this.stepMenu(step, time);
    if (this.scene === "boss") return this.stepBoss(step, time);
    return this.stepPlay(step, time);
  }

  // ── scenes ──────────────────────────────────────────────────────────────────

  private stepMenu(step: number, time: number): void {
    const sc = SCENES.menu;
    const triad = sc.triads[this.bar];
    const root = sc.roots[this.bar];
    const beat = 60 / this.bpm;

    // Lush, slow pad on each bar (held two bars for overlap), plus a soft sub.
    if (step === 0) {
      for (const n of triad) this.pad(midi(n + 12), time, beat * 4, 0.035);
      this.sub(midi(root - 12), time, beat * 3.5, 0.12);
    }
    // Sparse, dreamy FM-bell arpeggio drifting over the chord.
    if (step === 2 || step === 7 || step === 10 || step === 13) {
      const note = triad[(step + this.bar) % triad.length] + 12;
      this.fmBell(midi(note), time, beat * 1.4, 0.05, 2, 60, true);
    }
    // No drums — keep it zen.
  }

  private stepPlay(step: number, time: number): void {
    const sc = SCENES.play;
    const triad = sc.triads[this.bar];
    const root = sc.roots[this.bar];
    const i = this.intensity;

    // Pad chord at the top of each bar (swells with intensity).
    if (step === 0) for (const n of triad) this.pad(midi(n), time, (60 / this.bpm) * 2, 0.04 + i * 0.03);

    // Bass: quarter-note roots that fill into a 16th "gallop" as it heats up.
    if (step % 4 === 0) {
      const seq = [root, root + 7, root + 12, root + 7];
      this.bass(midi(seq[(step / 4) | 0] - 12), time, 0.22, 0.26);
    }
    if (i > 0.45 && step % 2 === 1) this.bass(midi(root - 12), time, 0.1, 0.14); // gallop offbeats

    // Drums.
    if (step % 4 === 0) this.kick(time);
    if (step === 4 || step === 12) this.snare(time, 0.45);
    if (i > 0.2 && step % 2 === 0) this.hat(time, 0.1 + i * 0.16);
    if (i > 0.6 && step % 2 === 1) this.hat(time, 0.07);

    // Lead motif once the fight heats up (FM bell with echo).
    if (i > 0.5 && step % 4 === 2) {
      const deg = LEAD_PENTA[(this.bar * 2 + step) % LEAD_PENTA.length];
      this.fmBell(midi(57 + deg), time, 0.2, 0.08 + i * 0.05, 2, 80, true);
    }
  }

  private stepBoss(step: number, time: number): void {
    const sc = SCENES.boss;
    const triad = sc.triads[this.bar];
    const root = sc.roots[this.bar];
    const i = this.intensity;

    // Rhythmic pad stabs (shorter than play) for urgency.
    if (step === 0 || step === 8) for (const n of triad) this.pad(midi(n), time, 0.5, 0.05);

    // Driving 8th-note bass (root + octave bounce).
    if (step % 2 === 0) {
      const oct = step % 4 === 0 ? 0 : 12;
      this.bass(midi(root - 12 + oct), time, 0.16, 0.3);
    }

    // Drums: four-on-the-floor with an extra pickup, busy hats.
    if (step % 4 === 0 || step === 14) this.kick(time);
    if (step === 4 || step === 12) this.snare(time, 0.5);
    if (step % 2 === 0) this.hat(time, 0.14);
    if (i > 0.5 && step % 2 === 1) this.hat(time, 0.1);

    // Aggressive, metallic lead motif (higher FM index).
    if (step % 4 === 2 || step === 15) {
      const deg = LEAD_PENTA[(this.bar + step) % LEAD_PENTA.length];
      this.fmBell(midi(57 + deg), time, 0.18, 0.1, 3, 160, true);
    }
  }

  // ── voices ──────────────────────────────────────────────────────────────────

  /** Detuned dual-saw pad (analog width), into the filter bus. */
  private pad(freq: number, time: number, dur: number, gain: number): void {
    if (!this.ctx || !this.filter) return;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, time);
    g.gain.linearRampToValueAtTime(gain, time + 0.25);
    g.gain.setValueAtTime(gain, time + dur * 0.6);
    g.gain.exponentialRampToValueAtTime(0.0001, time + dur);
    g.connect(this.filter);
    for (const det of [-7, 8]) {
      const o = this.ctx.createOscillator();
      o.type = "sawtooth";
      o.frequency.value = freq;
      o.detune.value = det + (Math.random() * 4 - 2); // slight analog drift
      o.connect(g);
      o.start(time);
      o.stop(time + dur + 0.05);
    }
  }

  /** Sawtooth bass into the filter bus. */
  private bass(freq: number, time: number, dur: number, gain: number): void {
    if (!this.ctx || !this.filter) return;
    const o = this.ctx.createOscillator();
    o.type = "sawtooth";
    o.frequency.value = freq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, time);
    g.gain.linearRampToValueAtTime(gain, time + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, time + dur);
    o.connect(g);
    g.connect(this.filter);
    o.start(time);
    o.stop(time + dur + 0.02);
  }

  /** Soft sine sub (menu warmth). */
  private sub(freq: number, time: number, dur: number, gain: number): void {
    if (!this.ctx || !this.filter) return;
    const o = this.ctx.createOscillator();
    o.type = "sine";
    o.frequency.value = freq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, time);
    g.gain.linearRampToValueAtTime(gain, time + 0.4);
    g.gain.exponentialRampToValueAtTime(0.0001, time + dur);
    o.connect(g);
    g.connect(this.filter);
    o.start(time);
    o.stop(time + dur + 0.05);
  }

  /** 2-op FM "DX7" bell lead; routed dry + through the slap echo. */
  private fmBell(
    freq: number,
    time: number,
    dur: number,
    gain: number,
    ratio: number,
    index: number,
    useEcho: boolean,
    dest?: AudioNode,
  ): void {
    const out = dest ?? this.filter;
    if (!this.ctx || !out) return;
    const carrier = this.ctx.createOscillator();
    carrier.type = "sine";
    carrier.frequency.value = freq;
    const mod = this.ctx.createOscillator();
    mod.type = "sine";
    mod.frequency.value = freq * ratio;
    const modGain = this.ctx.createGain();
    modGain.gain.setValueAtTime(freq * (index / 100), time);
    modGain.gain.exponentialRampToValueAtTime(1, time + dur); // bell-like index decay
    mod.connect(modGain);
    modGain.connect(carrier.frequency);

    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, time);
    g.gain.linearRampToValueAtTime(gain, time + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, time + dur);
    carrier.connect(g);
    g.connect(out);
    if (useEcho && this.echo) g.connect(this.echo);

    mod.start(time);
    carrier.start(time);
    mod.stop(time + dur + 0.05);
    carrier.stop(time + dur + 0.05);
  }

  private kick(time: number): void {
    if (!this.ctx || !this.drumBus) return;
    const o = this.ctx.createOscillator();
    o.type = "sine";
    const g = this.ctx.createGain();
    o.frequency.setValueAtTime(130, time);
    o.frequency.exponentialRampToValueAtTime(45, time + 0.12);
    g.gain.setValueAtTime(0.6, time);
    g.gain.exponentialRampToValueAtTime(0.0001, time + 0.18);
    o.connect(g);
    g.connect(this.drumBus);
    o.start(time);
    o.stop(time + 0.2);
    this.duck(time); // sidechain pump on the synth bus
  }

  /** Snare with a short bright "gated-reverb"-style tail. */
  private snare(time: number, gain: number): void {
    if (!this.ctx || !this.drumBus || !this.noiseBuf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const f = this.ctx.createBiquadFilter();
    f.type = "highpass";
    f.frequency.value = 1400;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(gain, time);
    g.gain.exponentialRampToValueAtTime(0.0001, time + 0.18);
    src.connect(f);
    f.connect(g);
    g.connect(this.drumBus);
    src.start(time);
    src.stop(time + 0.2);

    // Gated-reverb feel: a brighter, abruptly-cut diffuse tail.
    const tail = this.ctx.createBufferSource();
    tail.buffer = this.noiseBuf;
    const tf = this.ctx.createBiquadFilter();
    tf.type = "bandpass";
    tf.frequency.value = 2600;
    const tg = this.ctx.createGain();
    tg.gain.setValueAtTime(0, time);
    tg.gain.linearRampToValueAtTime(gain * 0.5, time + 0.02);
    tg.gain.setValueAtTime(gain * 0.5, time + 0.1);
    tg.gain.linearRampToValueAtTime(0.0001, time + 0.13); // hard gate cut
    tail.connect(tf);
    tf.connect(tg);
    tg.connect(this.drumBus);
    tail.start(time);
    tail.stop(time + 0.16);
  }

  private hat(time: number, gain: number): void {
    if (!this.ctx || !this.drumBus || !this.noiseBuf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const f = this.ctx.createBiquadFilter();
    f.type = "highpass";
    f.frequency.value = 7000;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(gain, time);
    g.gain.exponentialRampToValueAtTime(0.0001, time + 0.05);
    src.connect(f);
    f.connect(g);
    g.connect(this.drumBus);
    src.start(time);
    src.stop(time + 0.06);
  }

  /** Sidechain: briefly duck the synth bus under each kick for that pump. */
  private duck(time: number): void {
    if (!this.ctx || !this.pump) return;
    const amt = 0.6; // how far down it ducks
    const recover = (60 / this.bpm) * 0.9;
    this.pump.gain.cancelScheduledValues(time);
    this.pump.gain.setValueAtTime(amt, time);
    this.pump.gain.linearRampToValueAtTime(1, time + recover);
  }
}
